/**
 * search.v1 ranking — the reference implementation of
 * docs/search/search-scoring.md. Sales and Pocket implement the same rules and
 * run the same golden file (docs/search/search-golden.json); keep this file a
 * literal transcription of the spec so the three cannot drift.
 */

export type SearchField = 'title' | 'code' | 'subtitle' | 'other';
export type MatchKind = 'exact' | 'prefix' | 'word_prefix' | 'substring' | 'subsequence' | 'typo';

export interface SearchRecord {
  entity: string;
  id: string;
  title: string;
  code?: readonly string[] | null;
  subtitle?: string | null;
  other?: readonly string[] | null;
  active?: boolean | null;
  updated_at?: string | null;
}

export interface RankedRecord<R extends SearchRecord = SearchRecord> {
  record: R;
  score: number;
  match: { field: SearchField; kind: MatchKind };
}

export interface RankOptions {
  /** Equivalence groups of terms (model callsigns + generic synonyms). */
  aliases?: readonly (readonly string[])[];
  /** The instant the 30-day recency boost is measured from. */
  now?: Date;
}

const FIELD_WEIGHTS: readonly (readonly [SearchField, number])[] = [
  ['title', 1],
  ['code', 1],
  ['subtitle', 0.6],
  ['other', 0.4],
];
const MAX_VARIANTS = 8;
const RECENT_MS = 30 * 24 * 60 * 60 * 1000;
const UNIT_CODE_SHAPE = /^[0-9a-z]{3}[0-9]/;
const DIGIT = /[0-9]/;

// ---------------------------------------------------------------- normalization

export function normalize(s: string): string {
  return s
    .normalize('NFKD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]/g, ' ')
    .replace(/ +/g, ' ')
    .trim();
}

export function compact(s: string): string {
  return normalize(s).replace(/ /g, '');
}

export function loose(s: string): string {
  return compact(s).replace(/[0-9]+/g, (run) => run.replace(/^0+(?=[0-9])/, ''));
}

/** Optimal-string-alignment Damerau–Levenshtein distance. */
export function osa(a: string, b: string): number {
  const m = a.length;
  const n = b.length;
  const d: number[][] = Array.from({ length: m + 1 }, (_, i) => {
    const row = new Array<number>(n + 1).fill(0);
    row[0] = i;
    return row;
  });
  for (let j = 0; j <= n; j += 1) d[0][j] = j;
  for (let i = 1; i <= m; i += 1) {
    for (let j = 1; j <= n; j += 1) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      let v = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + cost);
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
        v = Math.min(v, d[i - 2][j - 2] + 1);
      }
      d[i][j] = v;
    }
  }
  return d[m][n];
}

function isSubsequence(needle: string, hay: string): boolean {
  let i = 0;
  for (let j = 0; j < hay.length && i < needle.length; j += 1) {
    if (hay[j] === needle[i]) i += 1;
  }
  return i === needle.length;
}

// ---------------------------------------------------------------- query

interface PreparedQuery {
  qn: string;
  qc: string;
  ql: string;
  tokens: string[];
}

function prepare(q: string): PreparedQuery {
  const qn = normalize(q);
  return { qn, qc: qn.replace(/ /g, ''), ql: loose(q), tokens: qn.split(' ') };
}

/** Spec §2: the query first, then whole-query alias members, then per-token substitutions. */
export function expandQuery(query: string, aliases: readonly (readonly string[])[] = []): string[] {
  const qn = normalize(query);
  if (!qn) return [];
  const variants: string[] = [qn];
  const push = (v: string) => {
    if (variants.length < MAX_VARIANTS && !variants.includes(v)) variants.push(v);
  };
  const groups = aliases.map((g) => g.map(normalize).filter(Boolean));

  for (const group of groups) {
    if (group.includes(qn)) for (const member of group) if (member !== qn) push(member);
  }
  const tokens = qn.split(' ');
  tokens.forEach((token, i) => {
    for (const group of groups) {
      if (!group.some((m) => m === token && !m.includes(' '))) continue;
      for (const member of group) {
        if (member === token) continue;
        push([...tokens.slice(0, i), member, ...tokens.slice(i + 1)].join(' '));
      }
    }
  });
  return variants;
}

// ---------------------------------------------------------------- tiers

function tokenEdits(t: string, words: string[]): number | null {
  let best: number | null = null;
  for (const w of words) {
    if (w.startsWith(t)) return 0;
    if (DIGIT.test(t) || t.length < 4) continue;
    const d = Math.min(osa(t, w), osa(t, w.slice(0, t.length)));
    if (d <= 1 || (d === 2 && t.length >= 8)) best = best === null ? d : Math.min(best, d);
  }
  return best;
}

/** Spec §3: the first tier that applies, or null. */
export function tier(q: PreparedQuery, value: string): { points: number; kind: MatchKind } | null {
  const fn = normalize(value);
  if (!fn || !q.qn) return null;
  const fc = fn.replace(/ /g, '');
  const fl = loose(value);

  if (fn === q.qn || fc === q.qc || fl === q.ql) return { points: 1000, kind: 'exact' };

  const looseBoundaryOk = !DIGIT.test(q.ql[q.ql.length - 1] ?? '') || !DIGIT.test(fl[q.ql.length] ?? '');
  if (fn.startsWith(q.qn) || fc.startsWith(q.qc) || (fl.startsWith(q.ql) && looseBoundaryOk)) {
    return { points: 800, kind: 'prefix' };
  }

  const words = fn.split(' ');
  if (q.tokens.every((t) => words.some((w) => w.startsWith(t)))) return { points: 600, kind: 'word_prefix' };

  if (fc.includes(q.qc) || fl.includes(q.ql)) return { points: 400, kind: 'substring' };

  if (q.qc.length >= 3 && !DIGIT.test(q.qc) && isSubsequence(q.qc, fc)) {
    return { points: 250, kind: 'subsequence' };
  }

  let maxEdits = 0;
  for (const t of q.tokens) {
    const e = tokenEdits(t, words);
    if (e === null) return null;
    maxEdits = Math.max(maxEdits, e);
  }
  if (maxEdits === 1) return { points: 200, kind: 'typo' };
  if (maxEdits === 2) return { points: 120, kind: 'typo' };
  return null;
}

// ---------------------------------------------------------------- records

function fieldValues(record: SearchRecord, field: SearchField): readonly string[] {
  switch (field) {
    case 'title': return record.title ? [record.title] : [];
    case 'subtitle': return record.subtitle ? [record.subtitle] : [];
    case 'code': return record.code ?? [];
    case 'other': return record.other ?? [];
  }
}

function timeOf(record: SearchRecord): number | null {
  if (!record.updated_at) return null;
  const t = Date.parse(record.updated_at);
  return Number.isNaN(t) ? null : t;
}

/** Spec §4: best field × variant, plus boosts. Null when nothing matches. */
export function scoreRecord<R extends SearchRecord>(
  query: string,
  record: R,
  opts: RankOptions = {},
): RankedRecord<R> | null {
  const variants = expandQuery(query, opts.aliases).map(prepare);
  if (variants.length === 0) return null;

  let best: { score: number; field: SearchField; kind: MatchKind } | null = null;
  for (const [field, weight] of FIELD_WEIGHTS) {
    for (const value of fieldValues(record, field)) {
      for (const q of variants) {
        const t = tier(q, value);
        if (!t) continue;
        const s = t.points * weight;
        if (!best || s > best.score) best = { score: s, field, kind: t.kind };
      }
    }
  }
  if (!best) return null;

  let score = best.score;
  if (record.active) score += 50;
  const updated = timeOf(record);
  const now = (opts.now ?? new Date()).getTime();
  // 0 <= age <= 30 days: a FUTURE updated_at (clock skew, bad data) earns nothing.
  if (updated !== null) {
    const age = now - updated;
    if (age >= 0 && age <= RECENT_MS) score += 25;
  }
  if (record.entity === 'unit' && UNIT_CODE_SHAPE.test(variants[0].qc)) score += 2000;

  return { record, score, match: { field: best.field, kind: best.kind } };
}

function codePointCompare(a: string, b: string): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

/** Spec §5 ordering over every matching record. */
export function rankRecords<R extends SearchRecord>(
  query: string,
  records: readonly R[],
  opts: RankOptions = {},
): RankedRecord<R>[] {
  const ranked: RankedRecord<R>[] = [];
  for (const r of records) {
    const scored = scoreRecord(query, r, opts);
    if (scored) ranked.push(scored);
  }
  return ranked.sort((a, b) => {
    if (b.score !== a.score) return b.score - a.score;
    const ta = timeOf(a.record);
    const tb = timeOf(b.record);
    if (ta !== tb) {
      if (ta === null) return 1;
      if (tb === null) return -1;
      return tb - ta;
    }
    // A record may arrive without a title; it sorts as the empty string.
    const byTitle = codePointCompare(normalize(a.record.title ?? ''), normalize(b.record.title ?? ''));
    if (byTitle !== 0) return byTitle;
    return codePointCompare(`${a.record.entity}:${a.record.id}`, `${b.record.entity}:${b.record.id}`);
  });
}
